import { useEffect, useRef, useState, type RefObject } from "react";
import { type CatalogueSnapshot, deliveryRuleSummary, type StudyDetail } from "@probetalab/contracts";
import { get } from "../lib/api.js";
import { useQuery } from "../lib/useQuery.js";
import { Icon } from "./icons.js";
import { Alert, Button, Definition, Loading, Money, Sheet, TubeSwatch } from "./ui.js";

/** What the order composer knows about the item before the sheet loads anything. */
export interface StudyDetailItem {
  kind: "study" | "package";
  id: string;
  clave: string;
  name: string;
  amount_cents: number | null;
}

const DASH = "—";

/**
 * The lookups (areas, containers, methods…) and packages come from the catalogue snapshot, which
 * is ~1 MB: it is fetched the first time a detail is opened and kept for a few minutes, so a
 * reception shift does not download it per patient and an edit in the catalogue shows up soon.
 */
const SNAPSHOT_TTL_MS = 5 * 60 * 1000;
let snapshotCache: { at: number; data: CatalogueSnapshot } | null = null;
async function loadSnapshot(): Promise<CatalogueSnapshot> {
  if (snapshotCache && Date.now() - snapshotCache.at < SNAPSHOT_TTL_MS) return snapshotCache.data;
  const data = await get("catalogue");
  snapshotCache = { at: Date.now(), data };
  return data;
}
/** Tests only. */
export function resetStudyDetailCache() {
  snapshotCache = null;
}

export function deliveryText(days: number | null, byWeekday?: readonly number[] | null): string {
  if (byWeekday) return deliveryRuleSummary({ delivery_days: null, delivery_by_weekday: byWeekday });
  return days === null ? DASH : days === 0 ? "Mismo día" : days === 1 ? "1 día" : `${days} días`;
}

/**
 * Read-only ficha of a study or package, opened from the order composer (Toronja's popup on the
 * new-order screen): sample, container, method, indications, analytes; for a package, its member
 * studies, each of which opens its own ficha. Escape or «Listo» closes it and focus goes back to
 * `returnFocus` (the study search), so the receptionist keeps scanning.
 */
export function StudyDetailSheet({ item, onClose, returnFocus }: { item: StudyDetailItem | null; onClose: () => void; returnFocus?: RefObject<HTMLElement | null> }) {
  const [memberId, setMemberId] = useState<string | null>(null);
  const open = item !== null;
  const snap = useQuery(loadSnapshot, [], { enabled: open });

  useEffect(() => setMemberId(null), [item?.kind, item?.id]);

  // Runs after the Sheet's own effect (children first) closed the <dialog>, which restores focus
  // to the button that opened it; the search box is where the next scan goes.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) returnFocus?.current?.focus();
    wasOpen.current = open;
  }, [open, returnFocus]);

  const pkg = item?.kind === "package" ? (snap.data?.packages.find((p) => p.id === item.id) ?? null) : null;
  const studyId = item?.kind === "study" ? item.id : memberId;
  const title = item ? `${item.clave} · ${item.name}` : "";

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      wide
      footer={
        <Button variant="primary" onClick={onClose}>
          Listo
        </Button>
      }
    >
      {item && (
        <div className="stack-lg">
          {item.kind === "package" && memberId === null && <PackageBody item={item} snap={snap.data} loading={snap.loading} error={snap.error} pkg={pkg} onOpen={setMemberId} />}
          {item.kind === "package" && memberId !== null && (
            <div>
              <Button variant="ghost" size="sm" icon="chevronLeft" onClick={() => setMemberId(null)}>
                Volver al paquete
              </Button>
            </div>
          )}
          {studyId && <StudyBody key={studyId} studyId={studyId} snap={snap.data} amountCents={item.kind === "study" ? item.amount_cents : undefined} showHeading={item.kind === "package"} />}
        </div>
      )}
    </Sheet>
  );
}

function defaultPrice(snap: CatalogueSnapshot | null, studyId: string): number | null {
  const list = snap?.price_lists.find((p) => p.is_default);
  return snap?.prices.find((p) => p.price_list_id === list?.id && p.study_id === studyId)?.amount_cents ?? null;
}

function StudyBody({ studyId, snap, amountCents, showHeading }: { studyId: string; snap: CatalogueSnapshot | null; amountCents: number | null | undefined; showHeading: boolean }) {
  const study = useQuery(() => get("study", { params: { id: studyId } }), [studyId]);
  if (study.loading && !study.data) return <Loading />;
  if (!study.data) return <Alert tone="red">{study.error ?? "No se encontró el estudio."}</Alert>;
  const s = study.data;
  // Names resolve once the snapshot arrives; until then «…», never a wrong «—».
  const name = (xs: { id: string; name: string }[] | undefined, id: string | null) => (id ? (xs ? (xs.find((x) => x.id === id)?.name ?? DASH) : "…") : DASH);
  const container = s.container_id ? snap?.containers.find((c) => c.id === s.container_id) : undefined;
  const price = amountCents === undefined ? defaultPrice(snap, s.id) : amountCents;
  return (
    <div className="stack-lg">
      {showHeading && (
        <h3 className="h3">
          <span className="mono muted" style={{ marginRight: 8 }}>
            {s.clave}
          </span>
          {s.name}
        </h3>
      )}
      <div className="grid-2" style={{ rowGap: 14 }}>
        <Definition k="Área" v={name(snap?.areas, s.area_id)} />
        <Definition k="Tipo de muestra" v={name(snap?.sample_types, s.sample_type_id)} />
        <Definition k="Recipiente" v={!s.container_id ? DASH : container ? <TubeSwatch hex={container.colour_hex} name={container.name} /> : snap ? DASH : "…"} />
        <Definition k="Método" v={name(snap?.methods, s.method_id)} />
        <Definition k="Técnica" v={name(snap?.techniques, s.technique_id)} />
        <Definition k="Equipo" v={name(snap?.equipment, s.equipment_id)} />
        <Definition k="Días de entrega" v={deliveryText(s.delivery_days, s.delivery_by_weekday)} />
        <Definition k="Precio" v={price === null ? DASH : <Money cents={price} />} />
      </div>
      <Definition k="Indicaciones para el paciente" v={s.indications?.trim() || DASH} />
      <AnalyteTable study={s} />
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { text: "Texto", reference: "Opciones", document: "Documento" };

export function AnalyteTable({ study }: { study: StudyDetail }) {
  const analytes = study.analytes.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const count = analytes.filter((a) => a.kind !== "subtitle").length;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="kicker">Analitos ({count})</div>
      {count === 0 ? (
        <p className="faint">Este estudio no tiene analitos definidos.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Clave</th>
                <th>Analito</th>
                <th>Unidad</th>
              </tr>
            </thead>
            <tbody>
              {analytes.map((a) =>
                a.kind === "subtitle" ? (
                  <tr key={a.id} style={{ background: "var(--paper-2)" }}>
                    <th colSpan={3} scope="rowgroup" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ink-2)", fontSize: "0.8125rem" }}>
                      {a.name}
                    </th>
                  </tr>
                ) : (
                  <tr key={a.id}>
                    <td className="mono muted">{a.code}</td>
                    <td>{a.name}</td>
                    <td className="muted">{a.unit ?? (a.kind === "numeric" ? DASH : (KIND_LABEL[a.kind] ?? DASH))}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PackageBody({
  item,
  snap,
  loading,
  error,
  pkg,
  onOpen,
}: {
  item: StudyDetailItem;
  snap: CatalogueSnapshot | null;
  loading: boolean;
  error: string | null;
  pkg: CatalogueSnapshot["packages"][number] | null;
  onOpen: (studyId: string) => void;
}) {
  if (!snap) return loading ? <Loading /> : <Alert tone="red">{error ?? "No se pudo cargar el catálogo."}</Alert>;
  const members = (pkg?.study_ids ?? []).map((id) => snap.studies.find((s) => s.id === id)).filter((s): s is StudyDetail => !!s && s.is_active);
  return (
    <div className="stack-lg">
      <div className="grid-2" style={{ rowGap: 14 }}>
        <Definition k="Tipo" v="Paquete" />
        <Definition k="Precio" v={item.amount_cents === null ? DASH : <Money cents={item.amount_cents} />} />
      </div>
      <div className="stack" style={{ gap: 8 }}>
        <div className="kicker">Estudios del paquete ({members.length})</div>
        {members.length === 0 ? (
          <p className="faint">Este paquete aún no tiene estudios asignados.</p>
        ) : (
          <div className="study-results" style={{ maxHeight: "none" }}>
            {members.map((s) => {
              const container = s.container_id ? snap.containers.find((c) => c.id === s.container_id) : undefined;
              return (
                <button key={s.id} type="button" className="study-hit" onClick={() => onOpen(s.id)} aria-label={`Ver detalle de ${s.name}`}>
                  <span className="clave">{s.clave}</span>
                  <span>
                    <div className="name">{s.name}</div>
                    <div className="meta">{s.indications?.trim() || (snap.areas.find((a) => a.id === s.area_id)?.name ?? "Sin área")}</div>
                  </span>
                  <span className="row" style={{ gap: 12 }}>
                    {container && <span className="tube-cap" style={{ background: container.colour_hex }} title={container.name} />}
                    <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
